import { RootAction } from 'MyTypes';
import { intersection, isEmpty, pick } from 'lodash/fp';
import { createReducer } from 'typesafe-actions';

import { AiChatFeedbackReaction } from '@portal/common/models/ai-chat-feedback';
import { DataProjectId } from '@portal/common/models/data-project';
import { DataResponse, OrderDirectionKey, PaginatedResponse } from '@portal/common/types';

import ReactionFilter, {
    ReactionFilterValue,
} from '../../scenes/InsightsScene/filters/ReactionFilter';
import {
    changeDataProjectId,
    changeExportIncludeIds,
    getInsightsCompanyUsageData,
    getInsightsData,
    initializeInsightsFilters,
    setVisualizationOptions,
    validateAndSetInsightsFilters,
} from './actions';
import {
    AggregationType,
    ChartType,
    DeliveryMethod,
    EventType,
    ReportingParam,
    TimeUnit,
    VisualizationType,
} from './models';
import { getInsightsDataTableColumns, getInsightsGroupByParams } from './selectors';

type DataTableParams = {
    page: number;
    perPage: number;
    orderDirection: null | OrderDirectionKey;
    orderBy: null | string;
};

export type EventTypeFilters = {
    aggregationType: AggregationType;
    aggregationValue: ReportingParam | null;
    chartType: ChartType;
    timeUnit: TimeUnit;
    groupBy: ReportingParam[];
    dataTableColumns: ReportingParam[];
    dataProjectId?: number[];
    dataTableParams: DataTableParams;
    reaction: ReactionFilterValue;
    deliveryMethod?: DeliveryMethod;
};

export type State = {
    eventType: EventType;
    insightsDataResponse: null | DataResponse | PaginatedResponse<ReportingParam>;
    insightsDataError: null | string;
    [EventType.visit]: EventTypeFilters;
    [EventType.pageView]: EventTypeFilters;
    [EventType.fileDownload]: EventTypeFilters;
    [EventType.dataQuery]: EventTypeFilters;
    [EventType.dataExport]: EventTypeFilters;
    [EventType.pdfExport]: EventTypeFilters;
    [EventType.userFeedback]: EventTypeFilters;
    [EventType.aiChat]: Pick<EventTypeFilters, 'chartType' | 'dataTableParams'> & {
        search: string;
    };
    [EventType.aiFeedback]: Pick<EventTypeFilters, 'chartType' | 'reaction' | 'dataTableParams'> & {
        search: string;
        reaction: '' | AiChatFeedbackReaction;
    };
    [EventType.conditionSearch]: EventTypeFilters;
    [EventType.companyUsage]: Omit<EventTypeFilters, 'dataTableParams'>;
    isExportIncludeIds?: boolean;
    fromDate: null | Date;
    toDate: null | Date;
    organizations: number[];
};

const FROM_NOW_30_DAYS = new Date(new Date().valueOf() - 1000 * 60 * 60 * 24 * 30);

export const defaultDataTableParams = {
    page: 1,
    perPage: 10,
    orderDirection: null,
    orderBy: null,
};

const defaultFiltersForEachEventType = {
    [EventType.visit]: {
        eventType: EventType.visit,
        aggregationType: AggregationType.count,
        aggregationValue: null,
        chartType: ChartType.bar,
        timeUnit: TimeUnit.week,
        groupBy: ['is_new_account'],
        dataTableColumns: [],
        dataTableParams: { ...defaultDataTableParams },
    },
    [EventType.pageView]: {
        eventType: EventType.pageView,
        aggregationType: AggregationType.count,
        aggregationValue: null,
        chartType: ChartType.table,
        timeUnit: TimeUnit.week,
        groupBy: ['country'],
        dataTableColumns: ['wildcarded_prev_app_path', 'wildcarded_app_path'],
        dataTableParams: { ...defaultDataTableParams },
    },
    [EventType.fileDownload]: {
        eventType: EventType.fileDownload,
        aggregationType: AggregationType.count,
        aggregationValue: null,
        chartType: ChartType.bar,
        timeUnit: TimeUnit.week,
        groupBy: ['resource_name'],
        dataTableColumns: [],
        dataTableParams: { ...defaultDataTableParams },
    },
    [EventType.dataQuery]: {
        eventType: EventType.dataQuery,
        aggregationType: AggregationType.count,
        aggregationValue: null,
        chartType: ChartType.table,
        timeUnit: TimeUnit.week,
        groupBy: ['country'],
        dataTableColumns: [
            'queried_data_types',
            'visible_filters',
            'cause_id',
            'sequela_id',
            'rei_id',
            'covariate_id',
            'sdoh_id',
            'location_id',
            'round_id',
        ],
        dataProjectId: [DataProjectId.GBD],
        dataTableParams: { ...defaultDataTableParams },
    },
    [EventType.dataExport]: {
        eventType: EventType.dataExport,
        aggregationType: AggregationType.count,
        aggregationValue: null,
        chartType: ChartType.table,
        timeUnit: TimeUnit.week,
        groupBy: ['country'],
        dataTableColumns: [
            'queried_data_types',
            'visible_filters',
            'cause_id',
            'sequela_id',
            'rei_id',
            'covariate_id',
            'sdoh_id',
            'location_id',
            'round_id',
        ],
        dataProjectId: [DataProjectId.GBD],
        dataTableParams: { ...defaultDataTableParams },
    },
    [EventType.pdfExport]: {
        eventType: EventType.pdfExport,
        aggregationType: AggregationType.count,
        aggregationValue: null,
        chartType: ChartType.table,
        timeUnit: TimeUnit.week,
        deliveryMethod: DeliveryMethod.download,
        groupBy: ['country'],
        dataTableColumns: [
            'chart_types',
            'pdf_report_options',
            'queried_data_types',
            'visible_filters',
            'cause_id',
            'sequela_id',
            'rei_id',
            'covariate_id',
            'sdoh_id',
            'location_id',
            'round_id',
        ],
        dataProjectId: [DataProjectId.GBD],
        dataTableParams: { ...defaultDataTableParams },
    },
    [EventType.userFeedback]: {
        eventType: EventType.userFeedback,
        aggregationType: AggregationType.count,
        aggregationValue: null,
        chartType: ChartType.bar,
        timeUnit: TimeUnit.week,
        groupBy: ['reason'],
        dataTableColumns: [],
        dataTableParams: { ...defaultDataTableParams },
    },
    [EventType.aiChat]: {
        chartType: ChartType.table,
        search: '',
        dataTableParams: { ...defaultDataTableParams },
    },
    [EventType.aiFeedback]: {
        chartType: ChartType.table,
        search: '',
        reaction: '',
        dataTableParams: { ...defaultDataTableParams },
    },
    [EventType.companyUsage]: {
        eventType: EventType.companyUsage,
        aggregationType: AggregationType.count,
        aggregationValue: null,
        timeUnit: {
            [VisualizationType.TotalVisits]: TimeUnit.day,
            [VisualizationType.TotalQueries]: TimeUnit.day,
            [VisualizationType.EngagementTrends]: TimeUnit.day,
        },
        chartType: ChartType.bar,
        dataTableColumns: [],
        dataProjectId: [],
        // groupBy: ['country'],
        visualizations: [],
        visualizationOptions: [],
        dataResponses: {},
        isLoading: false,
    },
    [EventType.conditionSearch]: {
        eventType: EventType.conditionSearch,
        aggregationType: AggregationType.count,
        aggregationValue: null,
        chartType: ChartType.table,
        timeUnit: TimeUnit.week,
        groupBy: ['search_term'],
        dataTableColumns: [
            'resource_id',
            'cause_id',
            'sequela_id',
            'rei_id',
            'cro_id',
            'pathogen_id',
            'risk_exposure_id',
            'covariate_id',
        ],
        dataTableParams: { ...defaultDataTableParams },
    },
};

const initialState: State = {
    ...defaultFiltersForEachEventType,
    eventType: EventType.visit,
    insightsDataResponse: null,
    insightsDataError: null,
    isExportIncludeIds: false,
    fromDate: FROM_NOW_30_DAYS,
    toDate: new Date(),
    organizations: [],
};

export default createReducer<State, RootAction>(initialState)
    .handleAction(initializeInsightsFilters, (state, action) => ({
        ...state,
        eventType: action.payload,
        organizations:
            action.payload !== EventType.companyUsage || state.organizations.length === 1
                ? state.organizations
                : [],
    }))
    .handleAction(changeDataProjectId, (state, action) => {
        const { eventType } = state;
        const { dataProjectId, datasets } = action.payload;

        if (eventType === EventType.companyUsage) {
            const { visualizations: currentVisualisations, dataResponses: currentDataResponses } =
                state[EventType.companyUsage];

            const visualizations = currentVisualisations.filter(
                ({ supportedDataProjectIds, globalData }) =>
                    !!globalData ||
                    isEmpty(supportedDataProjectIds) ||
                    (!isEmpty(supportedDataProjectIds) &&
                        !isEmpty(intersection(supportedDataProjectIds, dataProjectId)))
            );

            const nonAffectedVisualizations = visualizations
                .filter(({ globalData }) => !!globalData)
                .map(({ key }) => key);

            const dataResponses = pick(nonAffectedVisualizations, currentDataResponses);

            return {
                ...state,
                [eventType]: {
                    ...state[eventType],
                    visualizations,
                    dataProjectId,
                    dataResponses,
                },
            };
        }

        const filters = state[eventType];
        const defaultFilters = defaultFiltersForEachEventType[eventType];

        const availableGroupBy = getInsightsGroupByParams(eventType, datasets, dataProjectId);
        const intersectedValues = intersection(filters.groupBy, availableGroupBy);
        const groupBy = isEmpty(intersectedValues) ? defaultFilters.groupBy : intersectedValues;

        const defaultAndSelectedDataTableColumns = [
            ...defaultFilters.dataTableColumns,
            ...filters.dataTableColumns,
        ];
        const availableDataTableColumns = getInsightsDataTableColumns(
            eventType,
            datasets,
            dataProjectId
        );
        const dataTableColumns = intersection(
            defaultAndSelectedDataTableColumns,
            availableDataTableColumns
        );

        return {
            ...state,
            [eventType]: {
                ...filters,
                groupBy,
                dataTableColumns,
                dataTableParams: { ...defaultDataTableParams },
                dataProjectId,
            },
        };
    })
    .handleAction(validateAndSetInsightsFilters, (state, action) => action.payload)
    .handleAction(getInsightsData.request, (state, action) => ({
        ...state,
        insightsDataResponse: null,
        insightsDataError: null,
    }))
    .handleAction(getInsightsData.failure, (state, action) => ({
        ...state,
        insightsDataError: action.payload,
    }))
    .handleAction(getInsightsData.success, (state, action) => ({
        ...state,
        insightsDataResponse: action.payload,
    }))
    .handleAction(getInsightsCompanyUsageData.request, (state, action) => ({
        ...state,
        [EventType.companyUsage]: {
            ...state[EventType.companyUsage],
            isLoading: true,
        },
    }))
    .handleAction(getInsightsCompanyUsageData.success, (state, action) => ({
        ...state,
        [EventType.companyUsage]: {
            ...state[EventType.companyUsage],
            dataResponses: { ...state[EventType.companyUsage].dataResponses, ...action.payload },
            isLoading: false,
        },
    }))
    .handleAction(getInsightsCompanyUsageData.failure, (state, action) => ({
        ...state,
        [EventType.companyUsage]: {
            ...state[EventType.companyUsage],
            isLoading: false,
        },
    }))
    .handleAction(changeExportIncludeIds, (state, action) => ({
        ...state,
        isExportIncludeIds: action.payload,
    }))
    .handleAction(setVisualizationOptions, (state, action) => ({
        ...state,
        [EventType.companyUsage]: {
            ...state[EventType.companyUsage],
            visualizationOptions: action.payload,
        },
    }));
